import vueActivity from '@imt/vue-activity/src/store';
import alerts from '@imt/vue-admin-menus/src/store/alerts';
import notifications from '@imt/vue-admin-menus/src/store/notifications';
import {configuration} from '@imt/vue-configuration/src/store';
import {asString, defaultParamMap} from '@imt/vue-list/src/utils/queryParams';
import toolbox from '@imt/vue-toolbox/src/store';
import {deepCase} from '@imt/vue-toolbox/src/utils';
import utils, {axiosWithAuth, getCookieValue} from '@imt/vue-toolbox/src/utils';
import {vueVersioning} from '@imt/vue-versioning/src/store';
import {webFormEditor, authedAxios as webFormEditorAuthedAxios} from '@imt/vue-web-form-editor/src/store';
import kebabCase from 'lodash/kebabCase';
import Vue from 'vue';
import Vuex from 'vuex';

import fakeClientStore from '@/fake_client/store';
import featureFlags from '@/store/feature-flags';
import policySystem from '@/store/policy-system-front-end';
import wording from '@/utils';

Vue.use(Vuex);

export const authedAxios = axiosWithAuth();

// Both UIs dispatch the toolbox's `fetchCompanies`, but they cannot use the same upstream. Admin
// users hold `platform_admin_access`, so admin keeps the toolbox's direct platform-admin call.
// Agents do not, so the policy system front end -- where the web form editor's
// `available-companies` dropdown dispatches this -- goes through policy-data's proxy, which mints
// its own `platform_admin_access` service token and resolves role-derived permissions.
export const toolboxFetchCompanies = toolbox.actions.fetchCompanies;

// Read from `window.location` rather than importing the router: `@/router` calls `Vue.use(Router)`
// at import time, and pulling that into the store would install vue-router globally for every spec
// that imports `@/store`, shadowing the `$route` mocks those specs rely on. The router runs in
// history mode (router.js:8), so `pathname` is the live route path either way. The trailing
// `(\/|$)` matters: admin lives at `/admin` and under it (router.js:12), while the policy system
// front end is `/:url?/` (router.js:364), so a slug like `administration` must not match.
export function inAdmin() {
    return /^\/admin(\/|$)/.test(window.location.pathname);
}

// Override the toolbox's `fetchCompanies` to use the policy-data's proxy if not in the admin,
// which handles permissions for all users.
export async function fetchCompanies(context, queryString = '') {
    if (inAdmin()) {
        return await toolboxFetchCompanies(context, queryString);
    }

    const response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/companies/${queryString}`);

    context.commit('SET_COMPANIES', response.data);
}

toolbox.actions.fetchCompanies = fetchCompanies;

async function callApi(method = 'patch', url = '/', stuff = {}) {
    method = method.toLowerCase();

    return await authedAxios[method](
        url,
        method === 'delete' ? {} : utils.dataFormatter.serialize({stuff})
    );
}

export const getters = {
    policyLineOptions() {
        return [
            {display: 'Commercial', value: 'commercial'},
            {display: 'Personal', value: 'personal'},
        ];
    },
    pageTypeOptions() {
        return [
            {display: 'Other', value: 'other'},
            {display: 'Quote', value: 'quote'},
            {display: 'Submission', value: 'submission'},
        ];
    },
    transactionTypeOptions() {
        return [
            {display: 'Amendment', value: 'amendment'},
            {display: 'Cancellation', value: 'cancel'},
            {display: 'Inquiry', value: 'inquiry'},
            {display: 'New Business', value: 'new_business'},
            {display: 'Reapply', value: 'reapply'},
            {display: 'Reinstatement', value: 'reinstate'},
            {display: 'Renewal', value: 'renewal'},
            {display: 'Quote', value: 'quote'},
        ];
    },
};

export const actions = {
    async createPage(context, data) {
        const page = await context.dispatch('saveModel', {
            modelType: 'Page',
            data,
        });

        await context.dispatch('fetchPages', {
            page: '1',
            getVersionStatus: 'true',
        });

        return page;
    },
    async createDraft({state}, {versionId, itemType, itemId}) {
        const response = await authedAxios.post(
            `/policy-systems/${state.policySystemId}/${kebabCase(itemType)}s/${itemId}/versions/`,
            utils.dataFormatter.serialize({stuff: {id: versionId, type: 'Version'}})
        );

        return response.data ? utils.dataFormatter.deserialize(response.data) : {};
    },
    async fetchPolicySystems({commit}, queryObject = {}) {
        const queryString = asString(queryObject, {
                ...defaultParamMap,
                ...wording.searchParams,
            }),
            response = await authedAxios.get(
                `/policy-systems/?include=logo,created_by,updated_by${queryString}`
            );

        commit('SET_DATA', {field: 'policySystems', data: utils.dataFormatter.deserialize(response.data)});
        commit('SET_DATA', {field: 'totalPolicySystemCount', data: response.data.meta.pagination.count});

        return response;
    },
    async fetchEnvPolicySystems(context, targetEnvironment) {
        const response = await authedAxios.get(`/policy-systems/?environment=${targetEnvironment}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicySystem(context, {policySystemUrl, preview}) {
        const response = await authedAxios.get(
            `/policy-system-data/${policySystemUrl}/?include=logo,navigation-items.page${preview ? '&preview=true' : ''}`
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async importVersion(_, {pageId, data}) {
        const response = await authedAxios.post(`/pages/${pageId}/versions/import/`, data);

        return response.data;
    },
    async savePolicySystem(context, {data, method = 'post', url = '/policy-systems/'}) {
        const response = await authedAxios[method.toLowerCase()](
            url,
            utils.dataFormatter.serialize({stuff: {...data, type: 'PolicySystem'}})
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async removePolicySystemLogo({dispatch}, {logoId, policySystemId}) {
        await authedAxios.delete(`/policy-systems/${policySystemId}/logo/${logoId}/`);
        dispatch('fetchPolicySystems');
    },
    async fetchPages({state, commit}, queryObject = {}) {
        if (state.policySystemId) {
            const queryString = asString(queryObject, {
                    ...defaultParamMap,
                    ...wording.searchParams,
                    getVersionStatus: 'get_version_status',
                }),
                response = await authedAxios.get(
                    `/policy-systems/${state.policySystemId}/pages/?include=obsolete_by,latest_version.updated_by${queryString}`
                );

            commit('SET_DATA', {field: 'pages', data: utils.dataFormatter.deserialize(response.data)});
            commit('SET_DATA', {field: 'totalPageCount', data: response.data.meta.pagination.count});

            return utils.dataFormatter.deserialize(response.data);
        }
    },
    async fetchQuestionGroups({state, commit}, queryObject, includeWebForm = false) {
        let queryString = asString(queryObject, {
                ...defaultParamMap,
                ...wording.searchParams,
            }),
            url = `/policy-systems/${state.policySystemId}/question-groups/?include=obsolete_by,${includeWebForm ? 'versions.web_form.sections.inputs.options,' : ''}versions.updated_by,versions.reverted_by`,
            response = await authedAxios.get(`${url}${queryString}`);

        commit('SET_DATA', {field: 'questionGroups', data: utils.dataFormatter.deserialize(response.data)});
        if (response.data.meta?.pagination) {
            commit('SET_DATA', {field: 'totalQuestionGroupCount', data: response.data.meta.pagination.count});
        }

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchQuestionGroup({state}, {questionGroupId, includeWebForm}) {
        let url = `/policy-systems/${state.policySystemId}/question-groups/${questionGroupId}/?include=obsolete_by,versions.updated_by,versions.reverted_by${includeWebForm ? ',versions.web_form.sections.input_groups.inputs.options' : ''}`,
            response = await authedAxios.get(url);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchDiffs({state}, {oldVersionId, newVersionId, itemType, itemId}) {
        const response = await authedAxios.get(`/policy-systems/${state.policySystemId}/${kebabCase(itemType)}s/${itemId}/versions/${oldVersionId}/diffs/${newVersionId}/`);

        return deepCase(response.data, 'camel');
    },
    async fetchCompaniesWithLogos() {
        const response = await authedAxios.get(`${process.env.VUE_APP_ADMIN_URL_PLATFORM_ADMIN}/api/v1/companies/?include=logos`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchVersion({state}, {itemType, itemId, versionId, queryString = ''}) {
        const response = await authedAxios.get(`/policy-systems/${state.policySystemId}/${kebabCase(itemType)}s/${itemId}/versions/${versionId}/${queryString}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async saveModel(context, {data, modelType, method = 'post', modelId = null, action = ''}) {
        let model = {
            type: modelType
        };

        if (method !== 'post' && modelId) {
            model.id = modelId;
        }

        const response = await callApi(
            method,
            `/policy-systems/${context.state.policySystemId || data.policySystemId}/${kebabCase(modelType)}s/${modelId ? `${modelId}/` : ''}${action}${action.length ? '/' : ''}`,
            {...data, ...model}
        );

        return response.data ? utils.dataFormatter.deserialize(response.data) : {};
    },
    async saveVersion(context, {data, itemType, itemId, method = 'patch', versionId = null}) {
        const response = await callApi(
            method,
            `/policy-systems/${context.state.policySystemId}/${kebabCase(itemType)}s/${itemId}/versions/${versionId}/?include=updated_by`,
            {...data, type: 'Version', id: versionId}
        );

        return response.data ? utils.dataFormatter.deserialize(response.data) : {};
    },
    async searchForUser(context, {search = ''}) {
        const response = await authedAxios.get(`${process.env.VUE_APP_ADMIN_URL_USERS}/api/v1/users/?search=${search}`);

        return {
            data: utils.dataFormatter.deserialize(response.data),
            links: response.data.links,
            meta: response.data.meta,
        };
    },
    async impersonate(context, id = null) {
        await authedAxios.post(`${process.env.VUE_APP_ADMIN_URL_USERS}/api/v1/impersonate/${id ? `${id}/` : ''}`);
    },
    async fetchPage(context, {polId, pageId, preview}) {
        const response = await authedAxios.get(`/policy-system-data/${polId}/${pageId}/?include=action_bar.action_items,web_form.sections.input_groups.children.inputs.options,web_form.sections.input_groups.inputs.options${preview ? '&preview=true' : ''}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPageById({state}, {pageId, queryString = '?include=obsolete_by,versions.updated_by,versions.action_bar'}) {
        const response = await authedAxios.get(`/policy-systems/${state.policySystemId}/pages/${pageId}/${queryString}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchWebForm(context, {policySystemId, version}) {
        const response = await authedAxios.get(`/policy-systems/${policySystemId}/pages/${version.pageId}/versions/${version.id}/web-form/${version.webForm.id}/?include=sections.input_groups.inputs.options,sections.input_groups.children`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchMainNavigation(context, {url, data}) {
        const response = await authedAxios.get(url, {params: data}),
            navItems = utils.dataFormatter.deserialize(response.data);

        context.commit('SET_DATA', {
            field: 'pageNavigationItems',
            data: navItems.filter(i => i.page?.id),
        });

        return navItems.filter(i => !i.parent?.id);
    },
    async saveNavigationItem({state}, item) {
        item.type = 'NavigationItem';
        // Exclude relations from page to prevent max callstack errors when serializing data
        if (item.page) {
            item.page = {id: item.page.id, type: 'Page'};
        }

        const response = await callApi(
            item.id ? 'PATCH' : 'POST',
            `/policy-systems/${state.policySystemId || item.policySystemId}/navigation-items/${item.id ? item.id + '/' : ''}`,
            item
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async deleteItem({state}, itemId) {
        return callApi('delete', `/policy-systems/${state.policySystemId}/navigation-items/${itemId}/`);
    },
    async saveActionBar(context, {data, method = 'post', actionBarId = ''}) {
        actionBarId = (actionBarId ? `${actionBarId}/` : '');

        const response = await authedAxios[method.toLowerCase()](
            `/policy-systems/${context.state.policySystemId || data.policySystemId}/action-bars/${actionBarId}`,
            utils.dataFormatter.serialize({stuff: {...data, type: 'ActionBar'}})
        );

        if (context.state.policySystemId) {
            await context.dispatch('fetchActionBars', context.state.policySystemId);
        }

        return utils.dataFormatter.deserialize(response.data);
    },
    async deleteActionBar(context, actionBarId = '') {
        await authedAxios.delete(`/policy-systems/${context.state.policySystemId}/action-bars/${actionBarId}/`);

        await context.dispatch('fetchActionBars', context.state.policySystemId);
    },
    async fetchActionBar({state}, actionBarId) {
        const response = await authedAxios.get(
            `/policy-systems/${state.policySystemId}/action-bars/${actionBarId}/`
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchActionBars({commit, state}, queryObject = {}) {
        const queryString = asString(queryObject, {
                ...defaultParamMap,
                ...wording.searchParams,
            }),
            response = await authedAxios.get(
                `/policy-systems/${state.policySystemId}/action-bars/?include=updated_by${queryString}`
            );

        commit('SET_DATA', {field: 'actionBars', data: utils.dataFormatter.deserialize(response.data)});
        commit('SET_DATA', {field: 'totalActionBarCount', data: response.data.meta.pagination.count});

        return response;
    },
    async fetchPredefined(context) {
        const response = await authedAxios.get('/web-form/predefined/?filter[predefined_type]=web_form');

        context.commit('SET_DATA', {
            field: 'predefinedWebForms',
            data: utils.dataFormatter.deserialize(response.data),
        });

        return response;
    },
    async saveActionItem({state}, item) {
        // Exclude relations from page to prevent max callstack errors when serializing data
        if (item.data.page) {
            item.data.page = {id: item.data.page.id, type: 'Page'};
        }

        const urlPart = (item.data.id ? `${item.data.id}/` : ''),
            response = await callApi(
                item.data.id ? 'PATCH' : 'POST',
                `/policy-systems/${state.policySystemId || item.data.policySystemId}/action-items/${urlPart}`,
                item.data
            );

        return utils.dataFormatter.deserialize(response.data);
    },
    async deleteActionItem({state}, itemId) {
        return callApi('delete', `/policy-systems/${state.policySystemId}/action-items/${itemId}/`);
    },
    async fetchActionItems({state}, actionBarId = null) {
        let response = {};

        if (actionBarId) {
            // Fetch the items for an action bar.
            response = await callApi(
                'GET',
                `/policy-systems/${state.policySystemId}/action-bars/${actionBarId}/action-items/`,
                {type: 'actionItem'}
            );
        } else {
            // Fetch the predefined items.
            response = await callApi(
                'GET',
                `/policy-systems/${state.policySystemId}/action-items/?filter[is_predefined]=true`,
                {type: 'actionItem'}
            );
        }

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicySystemById(_, policySystemId) {
        const response = await authedAxios.get(`/policy-systems/${policySystemId}/?include=logo`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async uploadLogo(_, {logo, url}) {
        let formData = new FormData();
        formData.append('file', logo.file);

        let response = await authedAxios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': getCookieValue(),
            }
        });

        return utils.dataFormatter.deserialize(response.data);
    },
    async deletePolicySystem(_, id) {
        let response = await authedAxios.delete(`/policy-systems/${id}`);

        return response.data;
    },
    async fetchAskKodiakGroups() {
        const response = await authedAxios.get(`/ask-kodiak-groups/`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchAdminPermissions(context, {search = ''} = {}) {
        const response = await authedAxios.get(`${process.env.VUE_APP_ADMIN_URL_USERS}/api/v1/permissions/?search=${search}`);

        return utils.dataFormatter.deserialize(response.data);
    },
};


export const mutations = {
    SET_ADMIN_SIDE_MENU_COLLAPSED(state, collapsed) {
        state.admin.sideMenu.collapsed = collapsed;
    },
    CLEAR_ERROR_MESSAGES(state) {
        state.errorMessages = {};
    },
    CLEAR_HIDDEN_INPUTS(state) {
        state.hiddenInputs = {};
    },
    SET_DATA(state, {field, data}) {
        state[field] = data;
    },
    CLEAR_SEARCH_FIELDS(state) {
        state.searchFields = {};
    },
    SET_SEARCH_FIELDS(state, searchField) {
        if ((typeof searchField.value !== 'boolean' && !searchField.value) || (Array.isArray(searchField.value) && !searchField.value.length)) {
            Vue.delete(state.searchFields, searchField.key);
        } else {
            Vue.set(state.searchFields, searchField.key, searchField.value);
        }
    },
};

export const state = () => {
    return {
        admin: {
            sideMenu: {
                collapsed: true,
            },
        },
        actionBars: [],
        currentAdminPolicySystem: null,
        mergeViewQuery: {},
        pageMenus: [],
        pageNavigationItems: [],
        pages: [],
        policy: {},
        policySystemId: null,
        policySystemTitle: null,
        policySystems: [],
        predefinedWebForms: [],
        questionGroups: [],
        searchFields: {},
        totalPolicySystemCount: 0,
        totalActionBarCount: 0,
        totalPageCount: 0,
        totalQuestionGroupCount: 0,
    };
};

export const storeConfig = {
    modules: {
        alerts,
        configuration,
        fakeClientStore,
        featureFlags,
        notifications,
        policySystem,
        toolbox,
        vueActivity,
        vueVersioning,
        webFormEditor,
    },
    getters,
    state: state(),
    actions,
    mutations,
};

const store = new Vuex.Store(storeConfig),
    addTestSetParam = (config) => {
        const testSet = store.state.policySystem?.testSet;
        if (testSet) {
            config.params = { ...config.params, test_set: testSet };
        }

        return config;
    };

authedAxios.interceptors.request.use(addTestSetParam);
// adding the interceptor to the webFormEditorAuthedAxios instance to add the test_set parameter to the request
// this is needed because we call to fetch forms and class codes from WFE
// If/when these calls are moved to CMS, we can remove this interceptor
webFormEditorAuthedAxios.interceptors.request.use(addTestSetParam);

export default store;
